import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../../components/SearchableSelect';
import imputableService, {
  type ImputableCRUD,
  normalizeTipoCuentaCodigo,
  normalizeVisibleEnOption,
  VISIBLE_EN_OPTIONS,
} from '../../../services/imputableService';
import noImputableService, {
  formatNoImputableLabel,
  type NoImputable,
} from '../../../services/noImputableService';
import { getApiErrorMessage } from '../../../utils/apiError';

const NOM_CUENTA_MAX = 60;

const defaultForm: ImputableCRUD = {
  codImputable: '',
  nombre: '',
  imputable: 'S',
  tipo: '1',
  activo: 'N',
  visibleEn: 'Ninguno',
  codImputablePadre: '',
  descripcion: '',
  nroRubro: undefined,
};

const tiposImputable = [
  { value: '1', label: '1 — Activo' },
  { value: '2', label: '2 — Pasivo' },
  { value: '3', label: '3 — Ingresos' },
  { value: '4', label: '4 — Egresos' },
  { value: '5', label: '5 — Patrimonio neto' },
  { value: '6', label: '6 — Unidad' },
  { value: '9', label: '9 — Cuenta y orden' },
];

function rubroDisplayText(ni: NoImputable | null, nroRubro: number | undefined): string {
  if (ni) return formatNoImputableLabel(ni);
  if (nroRubro != null && nroRubro > 0) return `Rubro ${nroRubro}`;
  return '';
}

export interface PlanCuentaImputableDrawerProps {
  open: boolean;
  codImputable?: string | null;
  nroRubroPreset?: number | null;
  tipoPreset?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const PlanCuentaImputableDrawer: React.FC<PlanCuentaImputableDrawerProps> = ({
  open,
  codImputable,
  nroRubroPreset,
  tipoPreset,
  onClose,
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(codImputable);
  const [formData, setFormData] = useState<ImputableCRUD>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [noImputableSeleccionado, setNoImputableSeleccionado] = useState<NoImputable | null>(null);

  const [modalRubroOpen, setModalRubroOpen] = useState(false);
  const [modalRubroSearchInput, setModalRubroSearchInput] = useState('');
  const [modalRubroSearchTerm, setModalRubroSearchTerm] = useState('');
  const [modalRubroPage, setModalRubroPage] = useState(0);
  const [modalRubroRowsPerPage, setModalRubroRowsPerPage] = useState(10);
  const [modalRubroItems, setModalRubroItems] = useState<NoImputable[]>([]);
  const [modalRubroTotal, setModalRubroTotal] = useState(0);
  const [modalRubroLoading, setModalRubroLoading] = useState(false);

  const load = useCallback(async () => {
    if (!codImputable) return;
    try {
      setLoading(true);
      const imputable = await imputableService.getImputable(codImputable);
      if (!imputable) {
        enqueueSnackbar('Imputable no encontrado', { variant: 'error' });
        onClose();
        return;
      }
      const vis = normalizeVisibleEnOption(imputable.visibleEn);
      setFormData({
        ...defaultForm,
        ...imputable,
        tipo: normalizeTipoCuentaCodigo(imputable.tipo),
        visibleEn: vis,
        activo: vis === 'Ninguno' ? 'N' : 'S',
        nroRubro: imputable.nroRubro,
      });
      const nr = imputable.nroRubro;
      if (nr != null && nr > 0) {
        try {
          setNoImputableSeleccionado(await noImputableService.getNoImputable(nr));
        } catch {
          setNoImputableSeleccionado(null);
        }
      } else {
        setNoImputableSeleccionado(null);
      }
    } catch {
      enqueueSnackbar('Error al cargar el imputable', { variant: 'error' });
      onClose();
    } finally {
      setLoading(false);
    }
  }, [codImputable, enqueueSnackbar, onClose]);

  useEffect(() => {
    if (!open) return;
    setCurrentTab(0);
    if (isEdit) {
      void load();
      return;
    }
    const base: ImputableCRUD = { ...defaultForm };
    const nr =
      nroRubroPreset != null && nroRubroPreset > 0 ? nroRubroPreset : undefined;
    const tq = tipoPreset?.trim();
    const tipo =
      tq && ['1', '2', '3', '4', '5', '6', '9'].includes(tq) ? tq : undefined;
    setFormData({
      ...base,
      ...(nr != null ? { nroRubro: nr } : {}),
      ...(tipo ? { tipo } : {}),
    });
    setNoImputableSeleccionado(null);
    if (nr != null) {
      noImputableService
        .getNoImputable(nr)
        .then(setNoImputableSeleccionado)
        .catch(() => setNoImputableSeleccionado(null));
    }
  }, [open, isEdit, nroRubroPreset, tipoPreset, load]);

  useEffect(() => {
    const t = setTimeout(() => setModalRubroSearchTerm(modalRubroSearchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [modalRubroSearchInput]);

  const fetchModalNoImputables = useCallback(async () => {
    if (!modalRubroOpen) return;
    try {
      setModalRubroLoading(true);
      const res = await noImputableService.getNoImputables({
        searchTerm: modalRubroSearchTerm,
        pageNumber: modalRubroPage + 1,
        pageSize: modalRubroRowsPerPage,
      });
      setModalRubroItems(res.items);
      setModalRubroTotal(res.totalCount);
    } catch {
      setModalRubroItems([]);
      setModalRubroTotal(0);
    } finally {
      setModalRubroLoading(false);
    }
  }, [modalRubroOpen, modalRubroPage, modalRubroRowsPerPage, modalRubroSearchTerm]);

  useEffect(() => {
    if (modalRubroOpen) void fetchModalNoImputables();
  }, [modalRubroOpen, fetchModalNoImputables]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.codImputable.trim()) {
      enqueueSnackbar('El código del imputable es requerido', { variant: 'warning' });
      return;
    }
    if (!formData.nombre.trim()) {
      enqueueSnackbar('El nombre del imputable es requerido', { variant: 'warning' });
      return;
    }
    const nr = formData.nroRubro;
    if (nr == null || nr <= 0) {
      enqueueSnackbar('Debe seleccionar un no imputable (Nro. rubro)', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (isEdit && codImputable) {
        await imputableService.updateImputable(codImputable, formData);
      } else {
        await imputableService.createImputable(formData);
      }
      enqueueSnackbar('Imputable guardado exitosamente', { variant: 'success' });
      onSaved();
      onClose();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar el imputable'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 0 } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isEdit ? 'Editar imputable' : 'Nuevo imputable'}
            </Typography>
            <IconButton onClick={onClose} aria-label="Cerrar">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <form id="form-imputable-drawer" onSubmit={handleSubmit}>
                <Tabs value={currentTab} onChange={(_e, v) => setCurrentTab(v)} sx={{ mb: 2 }}>
                  <Tab label="General" sx={{ textTransform: 'none' }} />
                  <Tab label="Configuración" sx={{ textTransform: 'none' }} />
                </Tabs>

                {currentTab === 0 && (
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        name="codImputable"
                        label="Código"
                        value={formData.codImputable}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, codImputable: e.target.value }))
                        }
                        fullWidth
                        required
                        disabled={isEdit}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        name="nombre"
                        label="Nombre"
                        value={formData.nombre}
                        onChange={(e) => {
                          const t = e.target.value.trim();
                          setFormData((prev) => ({
                            ...prev,
                            nombre: t.length > NOM_CUENTA_MAX ? t.slice(0, NOM_CUENTA_MAX) : e.target.value,
                          }));
                        }}
                        onBlur={() =>
                          setFormData((prev) => ({
                            ...prev,
                            nombre: (prev.nombre || '').trim(),
                          }))
                        }
                        fullWidth
                        required
                        helperText={`${(formData.nombre || '').trim().length}/${NOM_CUENTA_MAX}`}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="descripcion"
                        label="Descripción"
                        value={formData.descripcion || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, descripcion: e.target.value }))
                        }
                        fullWidth
                        multiline
                        rows={3}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                          label="No imputable (Nro. rubro)"
                          value={rubroDisplayText(noImputableSeleccionado, formData.nroRubro)}
                          placeholder="Sin rubro asignado"
                          fullWidth
                          required
                          onClick={() => {
                            setModalRubroSearchInput('');
                            setModalRubroSearchTerm('');
                            setModalRubroPage(0);
                            setModalRubroOpen(true);
                          }}
                          helperText="Pulse el campo o la lupa para elegir el rubro."
                          sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 2, cursor: 'pointer' },
                            '& .MuiInputBase-input': { cursor: 'pointer' },
                          }}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  type="button"
                                  edge="end"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setModalRubroSearchInput('');
                                    setModalRubroSearchTerm('');
                                    setModalRubroPage(0);
                                    setModalRubroOpen(true);
                                  }}
                                  aria-label="Buscar no imputable"
                                >
                                  <SearchIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {(noImputableSeleccionado ||
                          (formData.nroRubro != null && formData.nroRubro > 0)) && (
                          <Button
                            type="button"
                            variant="text"
                            color="secondary"
                            onClick={() => {
                              setNoImputableSeleccionado(null);
                              setFormData((prev) => ({ ...prev, nroRubro: undefined }));
                            }}
                            sx={{ textTransform: 'none', mt: 1, flexShrink: 0 }}
                          >
                            Quitar
                          </Button>
                        )}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SearchableSelect
                        name="tipo"
                        label="Tipo de cuenta"
                        value={formData.tipo || null}
                        onChange={(v) => setFormData((prev) => ({ ...prev, tipo: v ?? '' }))}
                        options={tiposImputable}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SearchableSelect
                        name="visibleEn"
                        label="Visible en..."
                        value={normalizeVisibleEnOption(formData.visibleEn) || null}
                        onChange={(v) => {
                          const normalized = normalizeVisibleEnOption(v ?? '');
                          setFormData((prev) => ({
                            ...prev,
                            visibleEn: normalized,
                            activo: normalized === 'Ninguno' ? 'N' : 'S',
                          }));
                        }}
                        options={VISIBLE_EN_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="codImputablePadre"
                        label="Código Padre"
                        value={formData.codImputablePadre || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            codImputablePadre: e.target.value,
                          }))
                        }
                        fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>
                )}

                {currentTab === 1 && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.imputable === 'S'}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            imputable: e.target.checked ? 'S' : 'N',
                          }))
                        }
                      />
                    }
                    label="Es Imputable"
                  />
                )}
              </form>
            )}
          </Box>

          <Stack
            direction="row"
            spacing={1.5}
            sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
          >
            <Button
              type="submit"
              form="form-imputable-drawer"
              variant="contained"
              disabled={saving || loading}
              sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
            >
              {saving ? <CircularProgress size={22} /> : 'Guardar'}
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={saving}
              sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
            >
              Cancelar
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <Dialog
        open={modalRubroOpen}
        onClose={() => setModalRubroOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Buscar no imputable (rubro)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por Nro. rubro, nombre o descripción…"
              value={modalRubroSearchInput}
              onChange={(e) => {
                setModalRubroSearchInput(e.target.value);
                setModalRubroPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TableContainer
              sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. rubro</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre cuenta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Seleccionar
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalRubroLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : modalRubroItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No hay resultados
                      </TableCell>
                    </TableRow>
                  ) : (
                    modalRubroItems.map((row) => (
                      <TableRow key={row.nroRubro} hover>
                        <TableCell>{row.nroRubro}</TableCell>
                        <TableCell>{(row.nombreCuenta || '').trim() || '—'}</TableCell>
                        <TableCell>{(row.descripcioncta || '').trim() || '—'}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            type="button"
                            onClick={() => {
                              setNoImputableSeleccionado(row);
                              setFormData((prev) => ({ ...prev, nroRubro: row.nroRubro }));
                              setModalRubroOpen(false);
                            }}
                          >
                            Seleccionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 10, 20]}
              count={modalRubroTotal}
              rowsPerPage={modalRubroRowsPerPage}
              page={modalRubroPage}
              onPageChange={(_, p) => setModalRubroPage(p)}
              onRowsPerPageChange={(e) => {
                setModalRubroRowsPerPage(parseInt(e.target.value, 10));
                setModalRubroPage(0);
              }}
              labelRowsPerPage="Filas:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button type="button" onClick={() => setModalRubroOpen(false)} sx={{ textTransform: 'none' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlanCuentaImputableDrawer;
